import type { LabRow, MedicationRow, SourceRef } from "./types";

const ALLERGY_QUESTION_PATTERN = /allerg(?:y|ies) to medications? or foods?/i;
const ALLERGY_RESPONSE_PREFIX = "Allergies Response:";

export function valueOrDash(value: unknown): string {
  return value && String(value).trim().length > 0 ? String(value) : "--";
}

export function formatDate(value?: string | null): string {
  if (!value) return "--";

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const date = new Date(`${value}T00:00:00`);
    if (!Number.isNaN(date.getTime())) {
      return new Intl.DateTimeFormat("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      }).format(date);
    }
  }

  return value;
}

export function formatPhone(value?: string | null): string {
  if (!value) return "--";

  const digits = String(value).replace(/\D/g, "");
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  if (digits.length === 11 && digits.startsWith("1")) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }

  return value;
}

export function isAllergyMedication(medication: Pick<MedicationRow, "name" | "source">): boolean {
  const name = medication.name?.trim() || "";
  const sourceLabel = medication.source?.label?.trim() || "";

  return ALLERGY_QUESTION_PATTERN.test(sourceLabel) || name.startsWith(ALLERGY_RESPONSE_PREFIX);
}

export function allergyMedicationResponse(medication: Pick<MedicationRow, "name" | "source">): string {
  const name = medication.name?.trim() || "";
  if (!name) return "";
  if (!isAllergyMedication(medication)) return name;

  return name.startsWith(ALLERGY_RESPONSE_PREFIX)
    ? name.slice(ALLERGY_RESPONSE_PREFIX.length).trim()
    : name;
}

export function formatMedicationName(medication: Pick<MedicationRow, "name" | "source">): string {
  const name = medication.name?.trim() || "";

  if (!name) return "";
  if (!isAllergyMedication(medication)) return name;
  if (name.startsWith(ALLERGY_RESPONSE_PREFIX)) return name;

  return `${ALLERGY_RESPONSE_PREFIX} ${allergyMedicationResponse(medication)}`;
}

type SourceRow = { source?: SourceRef };

function sortableDateValue(value?: string | null): number | null {
  if (!value) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-").map(Number);
    return Date.UTC(year, month - 1, day);
  }

  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

export function sortLabRowsOldestToNewest(labs: LabRow[] | undefined): LabRow[] {
  return [ ...(labs || []) ].sort((left, right) => {
    const leftTime = sortableDateValue(left.date);
    const rightTime = sortableDateValue(right.date);

    if (leftTime != null && rightTime != null) return leftTime - rightTime;
    if (leftTime != null) return -1;
    if (rightTime != null) return 1;

    const leftDate = left.date?.trim() || "";
    const rightDate = right.date?.trim() || "";

    if (!leftDate && !rightDate) return 0;
    if (!leftDate) return 1;
    if (!rightDate) return -1;

    return leftDate.localeCompare(rightDate);
  });
}

export function rowsForForm<T extends SourceRow>(rows: T[] | undefined, emptyRow: T, extraRows: number): T[] {
  return [...(rows || []), ...Array.from({ length: extraRows }, () => ({ ...emptyRow, source: {} }))];
}
